/**
 * Questo file definisce il componente FlashcardItem.
 * - Rappresenta una singola flashcard con un fronte (domanda) e un retro (risposta).
 * - Gestisce l'animazione di "flip" (rotazione) al click.
 * - Mostra o nasconde i pulsanti di azione (gira, elimina) in base al contesto (vista pubblica o privata).
 * - Utilizza lo stato locale per tracciare se la card è girata o meno.
 * - Invoca funzioni passate come props per gestire la cancellazione.
 */

import React, { useState } from 'react';
import './FlashcardItem.css';

/**
 * Componente funzionale FlashcardItem.
 *
 * @param {object} props - Le props del componente.
 * @param {object} props.card - L'oggetto contenente i dati della flashcard (domanda, risposta).
 * @param {function} props.onDelete - La funzione da chiamare quando si clicca sul pulsante di eliminazione.
 * @param {boolean} [props.isPublicView=false] - Un flag per indicare se la card è in una vista pubblica. Se true, nasconde i controlli di modifica/eliminazione.
 * @returns {React.ReactElement} - Il JSX che renderizza la flashcard interattiva.
 */
const FlashcardItem = ({ card, onDelete, isPublicView = false }) => {
    // Stato locale per gestire se la flashcard è girata sul retro.
    // 'isFlipped' è un booleano, inizializzato a 'false' (mostra il fronte).
    const [isFlipped, setIsFlipped] = useState(false);

    /**
     * Gestore dell'evento click per girare la card.
     * - Inverte il valore booleano dello stato 'isFlipped'.
     * - Viene chiamato sia cliccando sulla card stessa sia sul pulsante "Gira".
     * @param {React.MouseEvent} e - L'oggetto evento del click.
     */
    const handleFlipClick = (e) => {
        // Non è necessario usare stopPropagation qui, poiché il click sulla card
        // e sul pulsante "Gira" devono entrambi eseguire la stessa azione.
        setIsFlipped(!isFlipped);
    };

    /**
     * Gestore dell'evento click per l'eliminazione della card.
     * - Ferma la propagazione dell'evento per evitare che il click sul cestino
     * attivi anche l'evento 'handleFlipClick' del contenitore padre.
     * - Chiama la funzione 'onDelete' passata come prop.
     * @param {React.MouseEvent} e - L'oggetto evento del click.
     */
    const handleDeleteClick = (e) => {
        // e.stopPropagation() previene che l'evento "salga" al contenitore 'flashcard-scene',
        // che altrimenti girerebbe la card invece di eliminarla.
        if (e) e.stopPropagation();
        // Esegue la funzione di callback per l'eliminazione.
        onDelete();
    };

    return (
        // Contenitore principale della flashcard.
        <div className="flashcard-container mb-4">
            {/* 'flashcard-scene' è il contenitore che gestisce la prospettiva 3D per l'animazione. */}
            {/* L'evento onClick su questa scena fa girare la card. */}
            <div className="flashcard-scene" onClick={handleFlipClick}>
                {/* 'flashcard-card' è l'elemento che ruota effettivamente. */}
                {/* La classe 'is-flipped' viene aggiunta/rimossa dinamicamente per attivare la trasformazione CSS. */}
                <div className={`flashcard-card ${isFlipped ? 'is-flipped' : ''}`}>
                    {/* Fronte della card (domanda). */}
                    <div className="card-face card-face-front">
                        <div className="card-content">
                            <small className="text-muted">Domanda</small>
                            <p>{card.domanda}</p>
                        </div>
                    </div>
                    {/* Retro della card (risposta). */}
                    <div className="card-face card-face-back">
                        <div className="card-content">
                            <small className="text-muted">Risposta</small>
                            <p>{card.risposta}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Renderizzazione condizionale dei pulsanti di azione. */}
            {/* Questi controlli vengono mostrati solo se 'isPublicView' è false. */}
            {!isPublicView && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                    {/* Pulsante per girare la card (azione alternativa al click diretto). */}
                    <button onClick={handleFlipClick} className="btn btn-sm btn-outline-primary">
                        <i className="bi bi-arrow-repeat me-1"></i> Gira
                    </button>
                    {/* Pulsante per eliminare la card. */}
                    <button onClick={handleDeleteClick} className="btn btn-sm btn-outline-danger btn-delete-icon">
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

export default FlashcardItem;